import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Building2, LogOut, Mail, RefreshCw } from "lucide-react";
import { useSession, signOut, useListOrganizations } from "@/lib/auth-client";

const POLL_INTERVAL_MS = 10000; // Check every 10 seconds

export function NoOrganization() {
  const { data: session } = useSession();
  const { data: orgs, refetch } = useListOrganizations();
  const [isChecking, setIsChecking] = useState(false);

  // Auto-poll for organization membership
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refetch]);

  // Reload when orgs become available
  useEffect(() => {
    if (orgs && orgs.length > 0) {
      window.location.reload();
    }
  }, [orgs]);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/login";
  };

  const handleManualRefresh = () => {
    setIsChecking(true);
    refetch();
    // Brief delay to show loading state, then check will happen via useEffect
    setTimeout(() => setIsChecking(false), 1000);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          p: { xs: 3, md: 4 },
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <img
            src="/logos/agent-edge.png"
            alt="Agent Edge"
            style={{ height: 48, width: "auto" }}
          />
        </Box>
        <Box
          sx={{
            mx: "auto",
            width: 64,
            height: 64,
            bgcolor: "info.lighter",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Building2 size={32} color="var(--mui-palette-info-main)" />
        </Box>

        <Typography
          variant="h5"
          fontWeight={700}
          color="text.primary"
          sx={{ mb: 1 }}
        >
          No Organization Access
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your account exists but you&apos;re not a member of any organization
          yet. An administrator needs to invite you to their team.
        </Typography>

        {session?.user.email && (
          <Box sx={{ bgcolor: "grey.100", borderRadius: 2, p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "text.primary",
              }}
            >
              <Mail size={16} />
              <Typography variant="body2" fontWeight={500}>
                {session.user.email}
              </Typography>
            </Box>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Once you&apos;ve been added to an organization, this page will
          automatically update.
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleManualRefresh}
            disabled={isChecking}
            startIcon={
              <RefreshCw
                size={16}
                className={isChecking ? "animate-spin" : ""}
              />
            }
            sx={{ flex: 1 }}
          >
            {isChecking ? "Checking..." : "Check Now"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleSignOut}
            startIcon={<LogOut size={16} />}
          >
            Sign Out
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
